import { MagnifyingGlass } from "@phosphor-icons/react"
import {
  Avatar,
  InputGroup,
  Selector,
  Tag,
  Tooltip,
} from "@vesatogo/grass-core"
import { DateInput } from "@vesatogo/grass-dates"
import { Cell, TableView } from "@vesatogo/grass-table"
import dayjs from "dayjs"
import { useEffect, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { siteConfig } from "~/constants/config"
import { AppRoutes } from "~/constants/routes"
import {
  Order_By,
  useAllUploaderListQuery,
  useUsersDetailQuery,
} from "~/generated/graphql"

const options = [
  { id: "booking_patti", name: "Transit Patti" },
  { id: "hishob_patti", name: "Hishob Patti" },
  { id: "net_payable_advance", name: "Payable/Receivable" },
  { id: "invoice_settlement", name: "Invoice Settlement" },
]

export const Uploader = () => {
  const [params, setParams] = useSearchParams()
  const search = params.get("search")
  const type = params.get("type")
  const page = Number(params.get("page")) || 1
  const [date, setDate] = useState()
  const [option, setOption] = useState(
    options?.find(op => op?.id === type) || options[0]
  )

  useEffect(() => {
    params.delete("search")
    setParams(params)
  }, [])

  const COLUMNS = [
    {
      Header: "Created On",
      accessor: "date_created",
      Cell({ value }) {
        if (!value) return "--"
        return (
          <Cell
            title={dayjs(value).format("D MMM 'YY")}
            subtitle={dayjs(value).format("hh:mm a")}
          />
        )
      },
    },
    {
      Header: "Done On",
      accessor: "date_done",
      Cell({ value }) {
        if (!value) return "--"
        return (
          <Cell
            title={dayjs(value).format("D MMM 'YY")}
            subtitle={dayjs(value).format("hh:mm a")}
          />
        )
      },
    },
    {
      Header: "Task Id",
      accessor: "task_id",
      Cell({
        value,
        row: {
          original: { id },
        },
      }) {
        if (!value) return "--"
        return (
          <Cell
            title={
              <Link
                to={{
                  pathname: AppRoutes.uploaderTaskDetail(id),
                }}
              >
                {value}
              </Link>
            }
          />
        )
      },
    },

    {
      Header: "Create Count",
      accessor: "result",
      id: "create_count",
      Cell({ value }) {
        const createCount = JSON.parse(value)?.["create_count"]
        if (createCount === undefined) return "--"
        return (
          <Cell
            title={
              <Tag intent="info" className="!rounded-full">
                {createCount}
              </Tag>
            }
          />
        )
      },
    },
    {
      Header: "Update Count",
      accessor: "result",
      id: "update_count",
      Cell({ value }) {
        const updateCount = JSON.parse(value)?.["update_count"]
        if (updateCount === undefined) return "--"
        return (
          <Cell
            title={
              <Tag intent="primary" className="!rounded-full">
                {updateCount}
              </Tag>
            }
          />
        )
      },
    },
    {
      Header: "Error Count",
      accessor: "result",
      id: "error_count",
      Cell({ value, row: { original } }) {
        const err = JSON.parse(value || "{}")?.errors
        const errorArray = [] as any
        for (const key in err) {
          for (const key2 in err[key]) {
            const errorMessage = err?.[key]?.[key2] || ""
            const idMatch = errorMessage.match(/id:\s*(\d+)/)
            if (idMatch) {
              errorArray.push({
                id: idMatch?.[1],
                error: errorMessage,
              } as any) // Push the object to the array
            } else {
              errorArray.push({ error: errorMessage } as any)
            }
          }
        }
        return (
          <Cell
            title={
              <Link
                to={{
                  pathname: AppRoutes.uploaderTaskDetail(original?.id),
                }}
              >
                <Tag propogateClick intent="danger" className="!rounded-full">
                  {errorArray?.length}
                </Tag>
              </Link>
            }
          />
        )
      },
    },

    {
      Header: "Uploaded By",
      accessor: "task_kwargs",
      Cell({ value, row }) {
        if (!value) return "--"
        const unescapedString = value.replace(/^"|"$/g, "")

        const cleanedString = unescapedString
          .replace(/'/g, '"')
          .replace(/(\w+):/g, '"$1":')
        const createdBy = JSON.parse(cleanedString)?.serialized_user

        const [{ data }] = useUsersDetailQuery({
          variables: {
            id: createdBy?.id,
          },
          pause: !createdBy?.id,
        })
        return (
          <Tooltip
            content={
              <div>
                <p>{data?.base_user_by_pk?.name || "na"}</p>
                <p className="text-gray-400">
                  {dayjs(data?.base_user_by_pk?.created_at).format(
                    "DD MMM 'YY | h:mm a"
                  )}
                </p>
              </div>
            }
          >
            <Avatar
              className="relative text-center"
              name={data?.base_user_by_pk?.name || ""}
              src={data?.base_user_by_pk?.photo?.url}
            />
          </Tooltip>
        )
      },
    },
  ]

  const [{ data, fetching }] = useAllUploaderListQuery({
    variables: {
      orderBy: {
        date_created: Order_By.DescNullsLast,
      },
      limit: siteConfig.pageSize,
      offset: (page - 1) * siteConfig.pageSize,
      where: {
        task_kwargs: option
          ? {
              _ilike: `%${option?.id}%`,
            }
          : undefined,

        task_id: {
          _eq: search ? search : undefined,
        },

        date_created: date
          ? {
              _gte: dayjs(date)?.startOf("day"),
              _lte: dayjs(date)?.endOf("day"),
            }
          : undefined,
        task_name: { _eq: "loader.load_file_task" },
      },
    },
  })
  const totalTasks =
    data?.django_celery_results_taskresult_aggregate?.aggregate?.count || 0

  return (
    <>
      <header className="grid grid-cols-3 p-2 border-b-1 border-b-gray-300">
        <div className="flex !w-full">
          <Selector
            options={options}
            value={options?.find(op => op?.id === type) || option}
            onChange={(e: any) => {
              setOption(e as any)
              params.delete("search")
              params.set("type", e?.id)
              setParams(params)
            }}
            className="!w-1/2"
            isClearable={false}
            intent="none"
          />
        </div>
        <div>
          <InputGroup
            className={"w-full"}
            inputProps={{
              value: search || "",
              placeholder: "Search",
              onChange(e) {
                params.set("search", e.target.value)
                setParams(params)
              },
            }}
            leftElement={<MagnifyingGlass />}
          />
        </div>
        <div className="flex gap-2 justify-end">
          <DateInput
            value={date}
            onChange={e => setDate(e as any)}
            className="!mr-2"
            excludeDate={date =>
              date <= dayjs().subtract(10, "days").toDate() ||
              date >= dayjs().toDate()
            }
          ></DateInput>
        </div>
      </header>
      <TableView
        relative={false}
        className="!h-[calc(100vh-64px-57px)]"
        paginationProps={{
          total: Math.ceil(totalTasks / siteConfig.pageSize),
          page: page,
          onChange(page) {
            params.set("page", page.toString())
            params.set("search", "")
            setParams(params)
          },
        }}
        columns={COLUMNS}
        isLoading={fetching}
        getRowProps={({ original }) => ({
          className:
            original?.seller?.user[0]?.is_blocked ||
            original?.buyer?.user[0]?.is_blocked
              ? "bg-red-100 hover:bg-red-100"
              : "",
        })}
        data={data?.django_celery_results_taskresult || []}
        meta={<>{totalTasks} Tasks</>}
      />
    </>
  )
}

export default Uploader
